import React, { useEffect } from "react";
import { motion, useAnimation, AnimatePresence } from "framer-motion";
import styled from "styled-components";

import step1Img from "../../assets/company-step-1.png";
import step2Img from "../../assets/company-step2.png";
import step3Img from "../../assets/company-step3.png";

const steps = [
  {
    id: 1,
    title: "Choose your tree",
    desc: "Choose to plant a real tree for you, family or friends and begin this unique restoration journey.",
    img: step1Img,
    icon: "🌱"
  },
  {
    id: 2,
    title: "We Nurture It",
    desc: "Our team cares for your tree on the ground. Wroot will water, monitor, and protect your friend as it grows.",
    img: step2Img,
    icon: "🌿"
  },
  {
    id: 3,
    title: "Stay Connected",
    desc: "Receive regular updates, photos, and thoughtful gifts tied to your tree’s progress.",
    img: step3Img,
    icon: "🌳"
  },
];

const HowItWorks = ({ onGetStarted }) => {
  const [activeStep, setActiveStep] = React.useState(0);
  const [isHovering, setIsHovering] = React.useState(false);
  const controls = useAnimation();

  // Auto-animation when not hovering - fixed dependency array
  useEffect(() => {
    let interval = null;
    
    // Only set interval if not hovering
    if (!isHovering) {
      interval = setInterval(() => {
        setActiveStep((prev) => (prev + 1) % steps.length);
      }, 5000);
      
      // Reset and start progress bar
      controls.set({ width: "0%" });
      controls.start({
        width: "100%",
        transition: { duration: 5, ease: "linear" }
      });
    } else {
      // Stop animation when hovering
      controls.stop();
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isHovering, controls]); // Removed activeStep dependency

  // Reset progress bar when step changes
  useEffect(() => {
    if (!isHovering) {
      controls.set({ width: "0%" });
      controls.start({
        width: "100%",
        transition: { duration: 5, ease: "linear" }
      });
    }
  }, [activeStep, isHovering, controls]);

  const handleStepClick = (index) => {
    setActiveStep(index);
    
    // Reset animation on manual step change
    controls.set({ width: "0%" });
    if (!isHovering) {
      controls.start({
        width: "100%",
        transition: { duration: 5, ease: "linear" }
      });
    }
  };

  return (
    <StyledContainer 
      onMouseEnter={() => setIsHovering(true)} 
      onMouseLeave={() => setIsHovering(false)}
    >
      <motion.h2 
        className="title"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        How it works
      </motion.h2>
      
      <motion.div 
        className="subtitle"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <p>Every tree you adopt helps restore our planet. Here's the journey:</p>
      </motion.div>

      <div className="content">
        <div className="steps-container">
          <div className="steps">
            {steps.map((step, index) => (
              <motion.div
                key={step.id}
                className={`step ${activeStep === index ? "active" : ""}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                onClick={() => handleStepClick(index)}
              >
                <motion.div 
                  className="step-number"
                  animate={{ 
                    backgroundColor: activeStep === index ? "var(--primary-color)" : "var(--background-color)",
                    color: activeStep === index ? "#1c3c24" : "var(--text-color)",
                    borderColor: activeStep === index ? "var(--primary-color)" : "var(--border-color)",
                    boxShadow: activeStep === index ? "0px 0px 0px 6px rgba(185, 226, 23, 0.3)" : "none"
                  }}
                  transition={{ duration: 0.3 }}
                >
                  {step.icon}
                </motion.div>
                <div className="step-content">
                  <h4>{step.title}</h4>
                  {activeStep === index && (
                    <motion.div
                      className="step-desc"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <p>{step.desc}</p>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="progress-container">
            <motion.div 
              className="progress-bar" 
              animate={controls}
            />
          </div>
        </div>

        <div className="visual-container">
          <div className="image-wrapper">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                className="image-container"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.5 }}
              >
                <img src={steps[activeStep].img} alt={steps[activeStep].title} />
                
                <motion.div 
                  className="floating-badge"
                  animate={{ 
                    y: [0, -10, 0],
                    rotate: [0, 5, 0, -5, 0]
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    repeatType: "loop"
                  }}
                >
                  Step {activeStep + 1}
                </motion.div>
                
                <div className="growing-tree">
                  <motion.div 
                    className="tree-animation"
                    animate={{
                      height: ["0%", "100%"],
                      opacity: [0.2, 1]
                    }}
                    transition={{
                      duration: 5,
                      repeat: 1,
                      repeatType: "reverse"
                    }}
                  />
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
          
          <div className="dots">
            {steps.map((_, index) => (
              <motion.div
                key={index}
                className={`dot ${activeStep === index ? "active" : ""}`}
                onClick={() => handleStepClick(index)}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Get Started Button */}
      <motion.div 
        className="get-started-container"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.5 }}
      >
        <motion.button
          className="get-started-btn"
          onClick={onGetStarted}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Get Started
        </motion.button>
      </motion.div>
    </StyledContainer>
  );
};

const StyledContainer = styled.section`
  width: var(--section-width);
  margin: var(--section-margin) auto;
  padding: 2rem 0;
  
  .title {
    text-align: center;
    color: var(--secondary-color);
    font-size: var(--l-heading);
    margin-bottom: 1rem;
  }
  
  .subtitle {
    text-align: center;
    max-width: 600px;
    margin: 0 auto 3rem;
    
    p {
      color: var(--text-gray-color);
    }
  }
  
  .content {
    display: flex;
    flex-wrap: wrap;
    gap: 3rem;
    align-items: center;
    justify-content: space-between;
  }

  .get-started-container {
    display: flex;
    justify-content: center;
    margin-top: 2rem;

    .get-started-btn {
      background: linear-gradient(135deg, var(--primary-color), #16a34a);
      color: white;
      border: none;
      padding: 1rem 2.5rem;
      font-size: 1.1rem;
      font-weight: 600;
      border-radius: 50px;
      cursor: pointer;
      transition: all 0.3s ease;
      box-shadow: 0 4px 15px rgba(34, 197, 94, 0.3);

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(34, 197, 94, 0.4);
      }

      &:active {
        transform: translateY(0);
      }
    }
  }
  
  .steps-container {
    flex: 1;
    min-width: 300px;
    
    .steps {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
    }
    
    .step {
      display: flex;
      align-items: flex-start;
      gap: 1rem;
      padding: 1rem;
      border-radius: var(--m-radius);
      cursor: pointer;
      transition: all 0.3s ease;
      
      &:hover {
        background-color: rgba(185, 226, 23, 0.05);
      }
      
      &.active {
        background-color: rgba(185, 226, 23, 0.1);
      }
      
      .step-number {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 48px;
        height: 48px;
        border-radius: 50%;
        border: 2px solid var(--border-color);
        font-size: 1.5rem;
        font-weight: 600;
        flex-shrink: 0;
      }
      
      .step-content {
        flex: 1;
        
        h4 {
          margin: 0 0 0.5rem;
          color: var(--text-color);
          font-weight: 600;
        }
        
        .step-desc p {
          margin: 0;
          line-height: 1.6;
          color: var(--text-gray-color);
        }
      }
    }
  }
  
  .progress-container {
    height: 4px;
    background-color: rgba(185, 226, 23, 0.2);
    border-radius: 2px;
    margin-top: 2rem;
    overflow: hidden;
    
    .progress-bar {
      height: 100%;
      width: 0%;
      background-color: var(--primary-color);
      border-radius: 2px;
    }
  }
  
  .visual-container {
    flex: 1;
    min-width: 300px;
    display: flex;
    flex-direction: column;
    align-items: center;
    
    .image-wrapper {
      position: relative;
      width: 100%;
      height: 400px;
      margin-bottom: 1.5rem;
      background-color: rgba(185, 226, 23, 0.05);
      border-radius: var(--l-radius);
      overflow: hidden;
    }
    
    .image-container {
      position: absolute;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      
      img {
        max-width: 80%;
        max-height: 80%;
        object-fit: contain;
        border-radius: var(--m-radius);
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
      }
      
      .floating-badge {
        position: absolute;
        top: 20px;
        right: 20px;
        background-color: var(--primary-color);
        color: var(--text-color);
        font-weight: 600;
        padding: 0.5rem 1rem;
        border-radius: 50px;
        box-shadow: 0 4px 15px rgba(185, 226, 23, 0.3);
      }
      
      
    }
    
    .dots {
      display: flex;
      gap: 0.75rem;
      
      .dot {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background-color: var(--border-color);
        cursor: pointer;
        transition: all 0.2s ease;
        
        &:hover {
          background-color: var(--gray-color);
        }
        
        &.active {
          background-color: var(--primary-color);
          transform: scale(1.2);
        }
      }
    }
  }
  
  @media (max-width: var(--bp-laptop)) {
    .content {
      flex-direction: column-reverse;
    }
    
    .visual-container {
      width: 100%;
      
      .image-wrapper {
        height: 350px;
      }
    }
  }
`;

export default HowItWorks;